import ApiService from './../api/ApiService';
import Config from '../constants/Config';
import Rate, { RateSync } from '../models/Rate';
import Urls from './../api/Urls';

const headersRate = {
    tenantId: Config.HOTEL_TENANT_ID,
    accept: 'text/plain',
    'Content-Type': 'application/json'
}

const sanitizeOperatingHours = (operatingHours = []) => (
    (operatingHours || []).map(item => ({
        startTime: item?.startTime,
        endTime: item?.endTime,
        isActive: item?.isActive !== undefined ? item.isActive : true,
        timeZoneOffset: item?.timeZoneOffset || '-7',
    }))
);

const sanitizeRatePayload = (payload = {}) => ({
    ...payload,
    operatingHours: sanitizeOperatingHours(payload.operatingHours),
});

export function getRateDescriptionTemplate() {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_DESCRIPTION_TEMPLATE,
        isDownload: true,
        headers: {
            ...headersRate,
            accept: '*/*',
        },
    }).get();
}

export function importRateDescription(hotelId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_DESCRIPTION_IMPORT,
        endpointParams: { hotelId },
        isDownload: true,
        headers: {
            tenantId: Config.HOTEL_TENANT_ID,
            accept: '*/*',
        },
    }).post(formData);
}

export function getRates(data) {
    const hotelId = data.hotelId;
    const params = {
        pageIndex: data.pageIndex || 1,
        pageSize: data.pageSize || 1000,
        ...data,
    };
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_LIST,
        endpointParams: { hotelId },
        parser: data => ratesDataParser(data, hotelId),
        params,
        headers: headersRate
    }).get();
}

export function getExcludedRatePlansByHotel(hotelId) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_LIST_EXCLUDED,
        endpointParams: { hotelId },
        parser: ratePlansDataParser,
        headers: headersRate
    }).get();
}

export function getRateDetail(id) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_DETAIL,
        endpointParams: { id },
        parser: (data) => new Rate(data),
        headers: headersRate
    }).get();
}

export function createRateTranslate(rate) {
    const payload = sanitizeRatePayload(rate.export(true).translate());
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_TRANSLATE_CREATE,
        headers: headersRate
    }).post(payload);
}

export function updateRateTranslate(payload) {
    const body = sanitizeRatePayload(payload.rate.export(true).translate());
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_TRANSLATE_UPDATE,
        headers: {
            ...headersRate,
            'accept-language': payload.language || 'vi-VN'
        }
    }).put(body);
}

export function deleteRateTranslate(id) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_TRANSLATE_DELETE,
        headers: headersRate,
        endpointParams: { id },
    }).delete();
}

export function sortRate(rates) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_SORT,
        headers: headersRate
    }).put(rates);
}

export function syncRatesAllHotels() {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_SYNC_ALL,
        headers: headersRate
    }).post();
}

export function syncRatesAll(payload) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_SYNC,
        headers: headersRate,
        parser: data => ratesDataSyncParser(data),
    }).post({ ...payload });
}

export function getRatesByHotels(hotelIds) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_LIST_BY_HOTELS,
        headers: headersRate
    }).post(hotelIds);
}

//cancel-policy
export function updateCancelPolicy(cancelPolicys) {
    let id = cancelPolicys.id || null;
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.HOTEL_CANCEL_POLICY_UPDATE,
        headers: headersRate,
        endpointParams: { id }
    }).put(cancelPolicys);
}

export function syncCancelPolicy(syncPolicys) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.HOTEL_CANCEL_POLICY_SYNC,
        headers: headersRate
    }).post(syncPolicys);
}

export function updateRateActive(data) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.UPDATE_RATE_ACTIVE,
        headers: headersRate,
        endpointParams: { id: data.id },
        params: data.data,
    }).put();
}

export function updateRateExtend(data) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.UPDATE_RATE_EXTEND,
        headers: headersRate,
        endpointParams: { id: data.id },
        params: data.data,
    }).put();
}

export function updateUsageStatus(data) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.UPDATE_USAGE_STATUS,
        headers: headersRate
    }).put(data);
}

export function syncRateLanguage(data) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.SYNC_RATE_LANGUAGE,
        headers: headersRate
    }).post(data)
}

const getRatePlanList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.result)) return data.result;
    if (Array.isArray(data?.ratePlans)) return data.ratePlans;
    return [];
}

const ratesDataParser = (data, hotelId) => ({
    dataList: getRatePlanList(data).map(item => new Rate({ ...item, hotelId })),
    pageIndex: data?.pageIndex || 1,
    pageSize: data?.pageSize || 10,
    totalCount: data?.totalCount || 0,
})

const ratePlansDataParser = (data) => {
    const ratePlans = Array.isArray(data) ? data
        : Array.isArray(data?.data) ? data.data
            : Array.isArray(data?.result) ? data.result
                : Array.isArray(data?.ratePlans) ? data.ratePlans
                    : [];

    return ratePlans.reduce((result, item) => {
        if (Array.isArray(item?.ratePlans)) {
            return result.concat(item.ratePlans.map(ratePlan => ({
                ...ratePlan,
                hotelId: item.id || item.hotelId
            })));
        }
        return result.concat(item);
    }, []);
}

const ratesDataSyncParser = (data) => ({
    result: (data || []).map(item => new RateSync(item))
})

export function autoTransalate(data) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_PLAN_AUTO_TRANSLATE,
        headers: headersRate
    }).post(data);
}

export function updateAutoTranslate(data) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.RATE_AUTO_TRANSLATE,
        headers: headersRate
    }).put(data);
}

export function createRateBeachVilla(data) {
    return new ApiService({
        baseURL: Config.API_PROMOTION,
        endpoint: Urls.CREATE_RATE_BEACH_VILLA,
        endpointParams: { hotelId: data.id },
        headers: headersRate
    }).post(data.rate);
}

export function getPriceConfigBeachVilla(payload) {
    return new ApiService({
        baseURL: Config.API_HOTEL,
        endpoint: Urls.GET_CONFIG_PRICE_BEACH_VILLA,
        params: payload,
        parser: data => data,
        headers: headersRate
    }).get();
}

export function createPriceConfigBeachVilla(data) {
    return new ApiService({
        baseURL: Config.API_HOTEL,
        endpoint: Urls.CREATE_CONFIG_PRICE_BEACH_VILLA,
        headers: headersRate
    }).post(data);
}

export function updatePriceConfigBeachVilla(data) {
    return new ApiService({
        baseURL: Config.API_HOTEL,
        endpoint: Urls.UPDATE_CONFIG_PRICE_BEACH_VILLA,
        headers: headersRate
    }).post(data);
}
